import { type StoreObject } from '@apollo/client/core';
import { useApolloClient, useQuery } from '@vue/apollo-composable';
import { computed, ref } from 'vue';

import {
  GetConversationMessagesDocument,
  MessageFilter,
  MessengerMessageDirection,
  MessengerMessageStatus,
  MessengerMessageType,
} from '@shared/api/graphql/gql/graphql';
import { clock, serializeInstant } from '@shared/lib/date-time';

import type { MessengerMessage } from './types';

import {
  useDeleteMessageMutation,
  useMarkConversationAsReadMutation,
  useSendMessageMutation,
} from '../api/messenger-mutations';
import { sortByMessageTime } from '../lib/compare-message-time';

const PER_PAGE = 15;
const MESSENGER_FILTERS = [MessageFilter.Scheduled, MessageFilter.NonScheduled] as const;

/**
 * Composable ленты сообщений диалога (GraphQL/Apollo).
 *
 * Источник правды — нормализованный Apollo-кэш (R5 keyFields по id): `messages`
 * — `computed` поверх `result`, без локального ref-зеркала. Оптимистика/WS пишут
 * в кэш: upsert через `cache.updateQuery` (дедуп по id — merge-политика R5),
 * удаление через `cache.modify` (в обход merge, чтобы список мог сжиматься) +
 * `evict`/`gc`. Пагинация — field-policy merge R5 (без ручного updateQuery).
 * Query чистая (D6): mark-as-read — явной мутацией при открытии диалога.
 * Публичная сигнатура сохранена (потребители MessengerPage не меняются).
 */
export const useMessengerMessages = () => {
  const { resolveClient } = useApolloClient();
  const currentConversationId = ref<string | null>(null);
  const showScheduledOnly = ref(false);

  const gqlFilter = computed(() => (showScheduledOnly.value ? MessageFilter.Scheduled : MessageFilter.NonScheduled));

  const sendMutation = useSendMessageMutation();
  const deleteMutation = useDeleteMessageMutation();
  const markReadMutation = useMarkConversationAsReadMutation();

  // beforeMessageId фиксирован на null; докачка старых идёт через fetchMore.
  const variables = computed(() => ({
    conversationId: currentConversationId.value ?? '',
    beforeMessageId: null as string | null,
    perPage: PER_PAGE,
    filter: gqlFilter.value,
  }));

  const enabled = computed(() => !!currentConversationId.value);

  const {
    result,
    loading,
    error: queryError,
    fetchMore,
    refetch,
  } = useQuery(GetConversationMessagesDocument, variables, () => ({
    enabled: enabled.value,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  }));

  // Кэш — источник правды: computed поверх нормализованного result (без ref-зеркала).
  const messages = computed<MessengerMessage[]>(() => result.value?.conversationMessages.messages ?? []);
  const scheduledMessagesCount = computed(() => result.value?.conversationMessages.scheduled_count ?? 0);
  const hasMore = computed(() => result.value?.conversationMessages.has_more ?? false);

  const sortedMessages = computed(() => sortByMessageTime(messages.value));

  const isLoading = computed(() => loading.value && messages.value.length === 0);
  const isLoadingMore = ref(false);
  const isSending = computed(() => sendMutation.isPending.value);
  const isDeleting = computed(() => deleteMutation.isPending.value);
  const error = computed(() => (queryError.value ? String(queryError.value) : null));

  // --- Операции над кэшем ленты (источник правды — Apollo cache) ---

  /**
   * Upsert сообщения в ленту текущего диалога через `cache.updateQuery`.
   * Merge-политика R5 дедуплицирует по id; нормализация обновляет сущность.
   */
  const writeMessageToCache = (message: MessengerMessage): void => {
    if (!currentConversationId.value) {
      return;
    }

    resolveClient().cache.updateQuery(
      { query: GetConversationMessagesDocument, variables: variables.value },
      (data) => {
        const page = data?.conversationMessages;
        const list = page?.messages ?? [];
        const exists = list.some((existing) => existing.id === message.id);
        const nextMessages = exists
          ? list.map((existing) => (existing.id === message.id ? { ...existing, ...message } : existing))
          : [...list, message];

        return {
          conversationMessages: {
            __typename: 'MessengerMessagePage',
            has_more: page?.has_more ?? false,
            scheduled_count: page?.scheduled_count ?? 0,
            messages: nextMessages,
          },
        };
      },
    );
  };

  /**
   * Удалить сообщение из ленты текущего диалога. `cache.modify` заменяет список
   * напрямую (в обход merge — иначе union-мёрж вернул бы удалённое), затем
   * evict+gc чистит нормализованную сущность (в т.ч. оптимистичный tempId).
   */
  const evictMessage = (id: string): void => {
    const conversationId = currentConversationId.value;
    if (!conversationId) {
      return;
    }

    const filter = gqlFilter.value;
    const cache = resolveClient().cache;

    cache.modify({
      fields: {
        conversationMessages(existing, { storeFieldName, readField }) {
          // cache.modify пробегает по всем вариациям поля — таргетим только текущий
          // диалог+фильтр (keyArgs сериализуются в storeFieldName).
          if (
            !storeFieldName.includes(`"conversationId":"${conversationId}"`) ||
            !storeFieldName.includes(`"filter":"${filter}"`)
          ) {
            return existing;
          }

          const page = existing as { messages?: readonly StoreObject[] } | undefined;
          if (!page?.messages) {
            return existing;
          }

          return {
            ...page,
            messages: page.messages.filter((messageRef) => readField('id', messageRef) !== id),
          };
        },
      },
    });

    cache.evict({ id: cache.identify({ __typename: 'MessengerMessage', id }) });
    cache.gc();
  };

  /**
   * Evict ленты конкретного диалога (обе фильтр-вариации) + gc — при смене диалога.
   */
  const evictConversationFeed = (conversationId: string): void => {
    const cache = resolveClient().cache;
    for (const filter of MESSENGER_FILTERS) {
      cache.evict({
        id: 'ROOT_QUERY',
        fieldName: 'conversationMessages',
        args: { conversationId: conversationId, filter },
      });
    }
    cache.gc();
  };

  /**
   * Обновить/добавить сообщение в ленте (для WS и оптимистичных отправок).
   */
  const addMessage = (message: MessengerMessage): void => {
    writeMessageToCache(message);
  };

  /**
   * Обновить сообщение (WS-статусы) — тот же upsert по id.
   */
  const updateMessage = (message: MessengerMessage): void => {
    writeMessageToCache(message);
  };

  /**
   * Удалить сообщение из ленты по id.
   */
  const removeMessage = (id: string): void => {
    evictMessage(id);
  };

  /**
   * Открыть диалог: загрузить сообщения и пометить прочитанным.
   * При смене диалога чистим ленту прошлого из кэша (evict+gc), а не обнуляем ref.
   *
   * `unreadCount` — количество непрочитанных в группе диалога (из кэша групп).
   * Гейт 4.4: не шлём mark-as-read, если непрочитанных заведомо нет (частый кейс —
   * большинство переходов между диалогами). `undefined` (группа ещё не в кэше,
   * напр. deep-link) трактуем как «возможно есть» и шлём — безопасный дефолт.
   */
  const fetch = async (conversationId: string, reset: boolean = true, unreadCount?: number): Promise<void> => {
    const previous = currentConversationId.value;
    currentConversationId.value = conversationId;

    if (reset) {
      if (previous && previous !== conversationId) {
        evictConversationFeed(previous);
      }
      await refetch();
    }

    // Query — чистая (D6): mark-as-read явной мутацией при открытии, но только
    // при наличии непрочитанных (либо когда счётчик неизвестен).
    if (unreadCount === undefined || unreadCount > 0) {
      void markReadMutation.mutateAsync(conversationId);
    }
  };

  /**
   * Загрузить более старые сообщения (докачка через field-policy merge R5).
   */
  const loadMore = async (): Promise<void> => {
    if (!currentConversationId.value || !hasMore.value || isLoadingMore.value || loading.value) {
      return;
    }

    // Курсор — хронологически старейшее из уже загруженных.
    const oldest = sortedMessages.value[0];
    if (!oldest) {
      return;
    }

    isLoadingMore.value = true;
    try {
      // Конкатенацию делает field-policy merge (R5); ручной updateQuery не нужен.
      await fetchMore({
        variables: {
          conversationId: currentConversationId.value,
          beforeMessageId: oldest.id,
          perPage: PER_PAGE,
          filter: gqlFilter.value,
        },
      });
    } finally {
      isLoadingMore.value = false;
    }
  };

  /**
   * Отправить сообщение с оптимистичным добавлением в кэш и explicit-swap
   * оптимистичной записи на серверную (evict temp → merge серверной по id).
   */
  const send = async (
    conversationId: string,
    body: string,
    targetConversationId: string,
    file?: File,
    messengerType?: string,
  ): Promise<MessengerMessage | null> => {
    const tempId = `optimistic-${Date.now()}`;
    const now = serializeInstant(clock.now(), 'millisecond');

    let messageType: MessengerMessage['message_type'] = MessengerMessageType.Text;
    if (file) {
      messageType = file.type.startsWith('image/') ? MessengerMessageType.Image : MessengerMessageType.Document;
    }

    const optimisticMessage: MessengerMessage = {
      __typename: 'MessengerMessage',
      id: tempId,
      direction: MessengerMessageDirection.Outgoing,
      sender_type: null,
      employee_name: null,
      message_type: messageType,
      messenger_type: messengerType ?? 'telegram',
      body,
      body_preview: null,
      status: MessengerMessageStatus.Pending,
      external_message_id: null,
      metadata: null,
      file_url: null,
      file_name: null,
      file_size: null,
      scheduled_at: null,
      sent_at: null,
      delivered_at: null,
      deliver_deadline: null,
      failed_reason: null,
      read_at: null,
      deleted_at: null,
      created_at: now,
      updated_at: now,
      conversation_id: conversationId,
      channel_id: null,
    };

    addMessage(optimisticMessage);

    try {
      const response = await sendMutation.mutateAsync({
        conversationId,
        body,
        targetConversationId,
        file,
      });

      // Explicit swap: убираем оптимистичное (evict temp), добавляем серверное
      // (merge по id — WS message.new не задвоит).
      removeMessage(tempId);
      addMessage(response);

      return response;
    } catch {
      removeMessage(tempId);
      return null;
    }
  };

  /**
   * Удалить сообщение (мутация + удаление из кэша ленты).
   */
  const deleteMsg = async (messageId: string): Promise<void> => {
    await deleteMutation.mutateAsync(messageId);
    removeMessage(messageId);
  };

  /**
   * Сбросить текущее состояние: снять активный диалог и вычистить его ленту из кэша.
   */
  const clear = (): void => {
    const previous = currentConversationId.value;
    currentConversationId.value = null;
    showScheduledOnly.value = false;
    if (previous) {
      evictConversationFeed(previous);
    }
  };

  /**
   * Переключить фильтр запланированных сообщений.
   */
  const toggleScheduledFilter = (): void => {
    if (!currentConversationId.value) {
      return;
    }

    showScheduledOnly.value = !showScheduledOnly.value;
  };

  return {
    // Данные
    messages,
    currentConversationId,
    showScheduledOnly,
    scheduledMessagesCount,

    // Состояние
    isLoading,
    isLoadingMore,
    isSending,
    isDeleting,
    hasMore,
    error,

    // Методы
    fetch,
    loadMore,
    send,
    delete: deleteMsg,
    clear,
    toggleScheduledFilter,
    addMessage,
    updateMessage,
    removeMessage,
  };
};
